// imageCompressor.js — Compress images before storing as base64 in MongoDB
// Prevents dashboard documents from exceeding the 16MB BSON limit

const MAX_DIMENSION = 1200;  // max width or height in px
const JPEG_QUALITY = 0.7;    // 0-1, 0.7 is good balance of quality vs size
const MAX_COMPRESSED_SIZE = 500 * 1024; // 500KB target per image

/**
 * Compress an image data URL to fit within size constraints.
 * Returns a compressed data URL (JPEG for photos, keeps PNG for transparency).
 * SVGs are returned as-is.
 */
export function compressImage(dataUrl, options = {}) {
  return new Promise((resolve) => {
    // SVGs don't need compression
    if (dataUrl.startsWith('data:image/svg+xml')) {
      resolve(dataUrl);
      return;
    }

    const maxDim = options.maxDimension || MAX_DIMENSION;
    const quality = options.quality || JPEG_QUALITY;

    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      // Scale down if needed
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Check if image has transparency (PNG with alpha)
      const hasAlpha = dataUrl.startsWith('data:image/png');

      if (hasAlpha) {
        // Check if actually uses transparency
        const imageData = ctx.getImageData(0, 0, width, height);
        const pixels = imageData.data;
        let usesAlpha = false;
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] < 250) { usesAlpha = true; break; }
        }

        if (usesAlpha) {
          // Keep as PNG but at reduced resolution
          resolve(canvas.toDataURL('image/png'));
          return;
        }
      }

      // Convert to JPEG for smaller size
      let result = canvas.toDataURL('image/jpeg', quality);

      // If still too large, reduce quality further
      if (result.length > MAX_COMPRESSED_SIZE * 1.37) { // base64 is ~37% larger than binary
        result = canvas.toDataURL('image/jpeg', 0.5);
      }
      if (result.length > MAX_COMPRESSED_SIZE * 1.37) {
        result = canvas.toDataURL('image/jpeg', 0.3);
      }

      resolve(result);
    };

    img.onerror = () => {
      // If compression fails, return original
      resolve(dataUrl);
    };

    img.src = dataUrl;
  });
}

/**
 * Estimate the byte size of a base64 data URL
 */
export function estimateBase64Size(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 0;
  // Remove the data:...;base64, prefix
  const base64 = dataUrl.split(',')[1] || dataUrl;
  return Math.ceil(base64.length * 0.75);
}
